import { useMemo } from 'react';
import { useToast } from '../contexts/ToastContext';
import {
  useProjectUserRefs,
  UseProjectUserRefsProps,
} from './useProjectUserRefs';

type UsersById = { [id: string]: { name?: string; email: string } };

export const useProjectUsersById = ({
  config,
  organizationId,
  projectId,
}: Omit<UseProjectUserRefsProps, 'includeArchived' | 'includeApiOnly'>) => {
  const { showUserFriendlyErrorToast } = useToast();
  const { users, ...rest } = useProjectUserRefs({
    config: {
      ...config,
      onError: (err) => {
        showUserFriendlyErrorToast(err, 'Unable to load user names');
        return config?.onError?.(err);
      },
    },
    organizationId,
    projectId,
    // Lookup by id, so extra archived and API-only users are harmless; always
    // including them keeps every caller on one shared cache entry.
    includeArchived: true,
    includeApiOnly: true,
  });

  const usersById = useMemo(() => {
    if (!users) return undefined;
    return users.reduce((acc, { id, name, email }) => {
      acc[id] = { name, email };
      return acc;
    }, {} as UsersById);
  }, [users]);

  return { usersById, ...rest };
};
